import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, Plus, Loader2, Pencil, XCircle, Eye, Link2, Users } from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';

const emptyForm = {
  title: '',
  description: '',
  start_datetime: '',
  end_datetime: '',
  location_text: '',
  neighborhood: '',
  registration_link: '',
  capacity_limit: '',
  display_start: new Date().toISOString().slice(0, 10),
  display_end: new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10),
};

export const OrgAtividadesTab = ({ orgId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/org-projects/${orgId}/activities`, { params: { include_past: true } });
      setActivities(res.data || []);
    } catch {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setForm({
      title: a.title || '',
      description: a.description || '',
      start_datetime: (a.start_datetime || '').slice(0, 16),
      end_datetime: (a.end_datetime || '').slice(0, 16),
      location_text: a.location_text || '',
      neighborhood: a.neighborhood || '',
      registration_link: a.registration_link || '',
      capacity_limit: a.capacity_limit ?? '',
      display_start: (a.display_start || '').slice(0, 10),
      display_end: (a.display_end || '').slice(0, 10),
    });
    setEditingId(a.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (form.title.trim().length < 3) { toast.error('Informe um título (mínimo 3 caracteres)'); return; }
    if (form.description.trim().length < 10) { toast.error('Descreva o evento (mínimo 10 caracteres)'); return; }
    if (!form.display_start || !form.display_end) { toast.error('Informe o período de exibição'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        capacity_limit: form.capacity_limit === '' ? null : Number(form.capacity_limit),
        start_datetime: form.start_datetime || null,
        end_datetime: form.end_datetime || null,
      };
      if (editingId) {
        await api.patch(`/activities/${editingId}`, payload);
        toast.success('Evento atualizado!');
      } else {
        await api.post(`/org-projects/${orgId}/activities`, payload);
        toast.success('Evento cadastrado! Ele já aparece na página da organização.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao salvar o evento');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async (a) => {
    const reason = window.prompt(`Encerrar "${a.title}"?\nInforme o motivo do encerramento:`);
    if (!reason) return;
    try {
      await api.post(`/activities/${a.id}/end`, { reason });
      toast.success('Evento encerrado');
      load();
    } catch {
      toast.error('Erro ao encerrar o evento');
    }
  };

  const fmtDate = (s) => {
    if (!s) return null;
    try { return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return s; }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500";

  return (
    <div className="space-y-6" data-testid="org-atividades-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" /> Eventos e Ações
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Cadastre eventos, campanhas e ações — eles aparecem na página pública da organização.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium whitespace-nowrap"
          data-testid="org-nova-atividade-btn"
        >
          <Plus className="w-4 h-4" /> Novo evento
        </button>
      </div>

      {showForm && (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5 space-y-4" data-testid="org-atividade-form">
          <h3 className="font-semibold text-gray-900">{editingId ? 'Editar evento' : 'Novo evento'}</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Feira de adoção de animais" className={inputCls} data-testid="atividade-titulo-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="O que vai acontecer, para quem é, como participar..." className={`${inputCls} resize-none`} data-testid="atividade-descricao-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Início do evento</label>
              <input type="datetime-local" value={form.start_datetime} onChange={(e) => setForm({ ...form, start_datetime: e.target.value })} className={inputCls} data-testid="atividade-inicio-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fim do evento</label>
              <input type="datetime-local" value={form.end_datetime} onChange={(e) => setForm({ ...form, end_datetime: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
              <input type="text" value={form.location_text} onChange={(e) => setForm({ ...form, location_text: e.target.value })}
                placeholder="Ex: Praça da Matriz" className={inputCls} data-testid="atividade-local-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
              <input type="text" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link de inscrição</label>
              <input type="text" value={form.registration_link} onChange={(e) => setForm({ ...form, registration_link: e.target.value })}
                placeholder="https://... (opcional)" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limite de vagas</label>
              <input type="number" min="1" value={form.capacity_limit} onChange={(e) => setForm({ ...form, capacity_limit: e.target.value })}
                placeholder="Deixe vazio para ilimitado" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exibir a partir de *</label>
              <input type="date" value={form.display_start} onChange={(e) => setForm({ ...form, display_start: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exibir até *</label>
              <input type="date" value={form.display_end} onChange={(e) => setForm({ ...form, display_end: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-xl font-medium"
              data-testid="atividade-salvar-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Salvar alterações' : 'Cadastrar evento'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
      ) : activities.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Nenhum evento cadastrado ainda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`atividade-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{a.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status === 'ativa' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {a.status === 'ativa' ? 'Ativa' : 'Encerrada'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{a.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                    {a.start_datetime && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(a.start_datetime)}</span>}
                    {a.location_text && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.location_text}</span>}
                    {a.capacity_limit && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{a.capacity_limit} vagas</span>}
                    {a.registration_link && <span className="flex items-center gap-1"><Link2 className="w-3.5 h-3.5" />Inscrição externa</span>}
                    <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{a.views || 0} visualizações</span>
                  </div>
                </div>
                {a.status === 'ativa' && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(a)} title="Editar"
                      className="p-2 rounded-lg text-gray-500 hover:bg-purple-50 hover:text-purple-600"
                      data-testid={`atividade-editar-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleEnd(a)} title="Encerrar"
                      className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                      data-testid={`atividade-encerrar-${a.id}`}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
